import {useEffect, useState} from 'react';
import {useNavigate} from 'react-router-dom';
import {useMutation, useQuery} from '@apollo/client';
import {Alert, Empty, Input, Modal, Pagination, Spin, Tag, Tooltip} from 'antd';
import {SearchOutlined, SyncOutlined} from '@ant-design/icons';
import classnames from 'classnames';
import moment from 'moment';
import {useSelector} from 'react-redux';
import {toast} from 'react-toastify';
import ExchangeSelect from 'components/exchangeSelect/ExchangeSelect';
import {makeSelectSubTabOperations} from 'redux/store/auth';
import {NavTabsEnum, isDev} from 'utils/constants';
import {useNewsExchange} from '../shared/useNewsExchange';
import {
  actionError,
  loadError,
  notDeployed,
  unionError,
} from '../shared/errors';
import {ActionButton, HowItWorks, TechnicalDetails, Tip} from '../shared/ui';
import {
  GET_NEWS_CLUSTERS,
  GET_NEWS_DRAFT_RUNS,
  DISPATCH_NEWS_CLUSTER,
  GET_STORY_COUNTS,
  RUN_NEWS_PIPELINE_TICK,
} from './resolvers';
import {CARD, FILTERS, HOW_IT_WORKS, PAGE, STATUS, STATUS_TIP} from './copy';
import {buildCards} from './storyCards';
import WebsitePicker from '../shared/WebsitePicker';
import {useDebounced} from '../shared/useDebounced';
import styles from '../shared/news.module.scss';

// The story operations sit on the News websites sub-tab in news-service.
export const ACTIVITY_SUB_TAB = 'news-sources';

const PAGE_SIZE = 20;

// The writing tool's runs live on the dev n8n. A production build links
// nowhere rather than to the dev instance.
const N8N_EXECUTIONS = isDev
  ? 'https://n8n.dabafinance.com/workflow/x9SIC1R4ixPSS3GR/executions'
  : null;

const StoryCard = ({card, canWrite, canOpen, onView, onReview, onWrite}) => {
  const status = STATUS[card.kind];
  const {run} = card;
  // A failed story can be sent again while it has tries left.
  const canRetry =
    card.kind === 'failed' &&
    run?.clusterId &&
    (run.attempts ?? 0) < (run.maxAttempts ?? 3);
  return (
    <li className={styles.card}>
      <div className={styles.cardHead}>
        <Tooltip title={STATUS_TIP[card.kind]}>
          <Tag color={status.color}>
            {status.icon} {status.label}
          </Tag>
        </Tooltip>
        <span className={styles.cardTitle}>{card.title}</span>
        <span className={styles.muted}>
          {card.time ? moment(card.time).fromNow() : ''}
        </span>
      </div>
      {card.articles.length || card.outlets.length ? (
        <p className={styles.muted}>
          <Tip label={CARD.reportedBy} tip={CARD.reportedByTip} />{' '}
          {card.articles.length
            ? card.articles.map((article, i) => (
                <span key={article.url || article.articleId || i}>
                  {i ? ' · ' : ''}
                  <Tooltip title={article.title}>
                    <a
                      className={styles.sourceLink}
                      href={article.url}
                      target="_blank"
                      rel="noreferrer">
                      {article.outlet} ↗
                    </a>
                  </Tooltip>
                </span>
              ))
            : card.outlets.join(' · ')}
        </p>
      ) : null}
      {card.sentence ? <p className={styles.reason}>{card.sentence}</p> : null}

      <div className={styles.cardActions}>
        {card.kind === 'published' && run?.websiteNewsId ? (
          <ActionButton
            allowed={canOpen}
            tip={CARD.viewTip}
            onClick={() => onView(run)}>
            {CARD.view}
          </ActionButton>
        ) : null}
        {card.kind === 'waiting' ? (
          <ActionButton
            allowed={canWrite}
            tip={CARD.writeNowTip}
            onClick={() => onWrite(card.clusterId, 'write')}>
            {CARD.writeNow}
          </ActionButton>
        ) : null}
        {canRetry ? (
          <ActionButton
            type="primary"
            allowed={canWrite}
            tip={CARD.tryAgainTip}
            onClick={() => onWrite(run.clusterId, 'retry')}>
            {CARD.tryAgain}
          </ActionButton>
        ) : null}
        {card.kind === 'review' && run?.draftId ? (
          <ActionButton
            type="primary"
            allowed={canOpen}
            tip={CARD.reviewTip}
            onClick={() => onReview(run)}>
            {CARD.review}
          </ActionButton>
        ) : null}
      </div>

      <TechnicalDetails label={CARD.technical}>
        <p className={styles.muted}>
          {CARD.storyId}: {run?.id || card.clusterId}
        </p>
        {run ? (
          <>
            <Tooltip title={CARD.triesTip}>
              <p className={styles.muted}>
                {CARD.tries(run.attempts ?? 0, run.maxAttempts ?? 3)}
              </p>
            </Tooltip>
            {run.n8nExecutionId ? (
              <p className={styles.muted}>
                {CARD.run}:{' '}
                {N8N_EXECUTIONS ? (
                  <a
                    href={`${N8N_EXECUTIONS}/${run.n8nExecutionId}`}
                    target="_blank"
                    rel="noreferrer">
                    {run.n8nExecutionId}
                  </a>
                ) : (
                  run.n8nExecutionId
                )}
              </p>
            ) : null}
            {run.lastError ? (
              <p className={styles.muted}>
                {CARD.error}: {run.lastError}
              </p>
            ) : null}
          </>
        ) : null}
      </TechnicalDetails>
    </li>
  );
};

const NewsActivity = () => {
  const navigate = useNavigate();
  const {exchangeId, selectExchange, waiting} = useNewsExchange();
  const [filterKey, setFilterKey] = useState('all');
  const [page, setPage] = useState(1);
  const [searchText, setSearchText] = useState('');
  const [sourceId, setSourceId] = useState();
  const search = useDebounced(searchText.trim()) || undefined;
  const ops = useSelector(state =>
    makeSelectSubTabOperations(state, NavTabsEnum.News, ACTIVITY_SUB_TAB),
  );
  // Review it and View article open Website News pages, which have their
  // own grant.
  const websiteNewsOps = useSelector(state =>
    makeSelectSubTabOperations(state, NavTabsEnum.News, 'website-news'),
  );
  const filter = FILTERS.find(f => f.key === filterKey);

  useEffect(() => setPage(1), [filterKey, exchangeId, sourceId, search]);
  useEffect(() => {
    setSearchText('');
    setSourceId(undefined);
  }, [exchangeId]);
  // The counts and both lists are narrowed the same way, so each chip counts
  // what it opens.
  const narrow = {sourceId, search};

  const queryOptions = {
    fetchPolicy: 'no-cache',
    notifyOnNetworkStatusChange: true,
    errorPolicy: 'all',
  };
  const countsQuery = useQuery(GET_STORY_COUNTS, {
    ...queryOptions,
    skip: !exchangeId,
    variables: {exchangeId, ...narrow},
  });
  const runsQuery = useQuery(GET_NEWS_DRAFT_RUNS, {
    ...queryOptions,
    skip: !exchangeId || !filter.runs,
    variables: {exchangeId, ...filter.runs, ...narrow, limit: PAGE_SIZE, page},
  });
  const clustersQuery = useQuery(GET_NEWS_CLUSTERS, {
    ...queryOptions,
    skip: !exchangeId || !filter.clusters,
    variables: {
      exchangeId,
      state: filter.clusters,
      ...narrow,
      limit: PAGE_SIZE,
      page,
    },
  });

  const refresh = () => {
    countsQuery.refetch();
    if (filter.runs) runsQuery.refetch();
    if (filter.clusters) clustersQuery.refetch();
  };

  const [runTick, {loading: checking}] = useMutation(RUN_NEWS_PIPELINE_TICK, {
    onError: actionError('check for new stories'),
  });
  const [dispatch] = useMutation(DISPATCH_NEWS_CLUSTER, {
    onError: actionError('write this story'),
  });

  // Write it now on a waiting story, Try again now on a failed one: both
  // write the story and hold it in Review. A refusal ("This story is already
  // being written.") is worded for editors, so it is shown as it comes.
  const write = (clusterId, why) =>
    Modal.confirm({
      title: why === 'retry' ? CARD.tryAgainTitle : CARD.writeNowTitle,
      content: why === 'retry' ? CARD.tryAgainConfirm : CARD.writeNowConfirm,
      okText: why === 'retry' ? CARD.tryAgain : CARD.writeNow,
      onOk: async () => {
        const {data} = await dispatch({variables: {exchangeId, clusterId}});
        const result = data?.dispatchNewsCluster;
        if (!result) return;
        if (result.__typename === 'Error') return toast.error(result.message);
        toast.success(CARD.sentForWriting);
        refresh();
      },
    });

  const listOf = (query, field, typename) => {
    const response = query.data?.[field];
    const ok = response?.__typename === typename;
    return {
      items: ok ? response.data.filter(Boolean) : [],
      pagination: ok ? response.pagination : null,
      error: response?.__typename === 'Error' ? response : query.error,
    };
  };
  const none = {items: [], pagination: null};
  const runs = filter.runs
    ? listOf(runsQuery, 'newsDraftRuns', 'NewsDraftRunList')
    : none;
  const clusters = filter.clusters
    ? listOf(clustersQuery, 'newsClusters', 'NewsClusterList')
    : none;
  const countsResponse = countsQuery.data?.newsStoryCounts;
  const counts =
    countsResponse?.__typename === 'NewsStoryCounts' ? countsResponse : null;
  const failure =
    runs.error ||
    clusters.error ||
    (countsResponse?.__typename === 'Error'
      ? countsResponse
      : countsQuery.error);

  const cards = buildCards({runs: runs.items, clusters: clusters.items});
  const loading = (runsQuery.loading || clustersQuery.loading) && !cards.length;

  // Where the list is. "All" pages through the sent and unsent lists
  // together, so it has as many pages as the longer of the two.
  const total =
    (runs.pagination?.total ?? 0) + (clusters.pagination?.total ?? 0);
  const pages = Math.max(
    runs.pagination?.pages ?? 0,
    clusters.pagination?.pages ?? 0,
    1,
  );
  const mixed = Boolean(filter.runs && filter.clusters);
  const from = Math.min((page - 1) * PAGE_SIZE + 1, total);
  const to = Math.min(page * PAGE_SIZE, total);
  let emptyText = PAGE.empty;
  if (search) emptyText = PAGE.emptySearch;
  else if (sourceId) emptyText = PAGE.emptyWebsite;
  const goTo = next => {
    setPage(next);
    window.scrollTo({top: 0, behavior: 'smooth'});
  };

  const countFor = f => {
    if (!counts) return null;
    if (f.count) return counts[f.count] ?? 0;
    return FILTERS.filter(other => other.count).reduce(
      (sum, other) => sum + (counts[other.count] ?? 0),
      counts.ready ?? 0,
    );
  };

  const checkNow = async () => {
    const {data} = await runTick({
      variables: {exchangeIds: [exchangeId], skipIngestion: false},
    });
    const result = data?.runNewsPipelineTick;
    if (!result) return;
    if (result.__typename === 'Error') {
      return unionError('check for new stories', result);
    }
    if (result.status === 'BUSY') return toast.info(PAGE.busy);
    if (result.errors?.length) console.error(result.errors);
    toast.success(PAGE.checked(result.ingested ?? 0, result.dispatched ?? 0));
    refresh();
  };

  // A run doesn't carry the article's language; the edit page takes either.
  const view = run =>
    navigate(`/dashboard/news/update-website-news/${run.websiteNewsId}/EN`);
  const review = run => navigate(`/dashboard/news/review/${run.draftId}`);

  return (
    <div className={styles.root}>
      <div className={styles.toolbar}>
        <span className={styles.tipLabel}>
          <Tip label={PAGE.exchange} tip={PAGE.exchangeTip} />
          <ExchangeSelect
            className={styles.exchangeSelect}
            value={exchangeId}
            onChange={selectExchange}
            placeholder="Pick an exchange"
          />
        </span>
        <ActionButton
          type="primary"
          icon={<SyncOutlined />}
          allowed={ops?.update}
          tip={PAGE.checkNowTip}
          disabled={!exchangeId}
          loading={checking}
          onClick={checkNow}>
          {PAGE.checkNow}
        </ActionButton>
      </div>

      {waiting ? (
        <Spin className={styles.spinner} />
      ) : !exchangeId ? (
        <Empty description={PAGE.pickExchange} />
      ) : (
        <>
          <HowItWorks id="activity">{HOW_IT_WORKS}</HowItWorks>

          <div className={styles.filters}>
            <Tooltip title={PAGE.searchTip}>
              <Input
                allowClear
                className={styles.searchBox}
                prefix={<SearchOutlined />}
                placeholder={PAGE.search}
                aria-label={PAGE.search}
                value={searchText}
                onChange={e => setSearchText(e.target.value)}
              />
            </Tooltip>
            <span className={styles.tipLabel}>
              <Tip label={PAGE.website} tip={PAGE.websiteTip} />
              <WebsitePicker
                className={styles.exchangeSelect}
                label={PAGE.website}
                exchangeId={exchangeId}
                value={sourceId}
                onChange={setSourceId}
              />
            </span>
          </div>

          <div className={styles.pills}>
            {FILTERS.map(f => {
              const count = countFor(f);
              return (
                <Tooltip key={f.key} title={f.tip}>
                  <button
                    type="button"
                    className={classnames(
                      styles.pill,
                      filterKey === f.key && styles.pillOn,
                    )}
                    onClick={() => setFilterKey(f.key)}>
                    {f.label}
                    {count !== null ? ` ${count}` : ''}
                  </button>
                </Tooltip>
              );
            })}
          </div>

          {failure ? (
            <Alert
              type={notDeployed(failure.message) ? 'info' : 'error'}
              showIcon
              message={loadError('these stories', failure)}
            />
          ) : null}

          {total && !failure ? (
            <p className={styles.muted}>
              {mixed
                ? PAGE.pageOf(total, page, pages)
                : PAGE.showing(from, to, total)}
            </p>
          ) : null}

          {loading ? <Spin className={styles.spinner} /> : null}
          {!loading && !failure && !cards.length ? (
            <Empty description={emptyText} />
          ) : null}

          <ul className={styles.cardList}>
            {cards.map(card => (
              <StoryCard
                key={card.key}
                card={card}
                canWrite={ops?.create}
                canOpen={websiteNewsOps?.view}
                onView={view}
                onReview={review}
                onWrite={write}
              />
            ))}
          </ul>

          <Pagination
            className={styles.pagination}
            current={page}
            pageSize={PAGE_SIZE}
            total={pages * PAGE_SIZE}
            showSizeChanger={false}
            hideOnSinglePage
            onChange={goTo}
          />
        </>
      )}
    </div>
  );
};

export default NewsActivity;
